import React, { useEffect, useState } from "react";
import { fetchAllDailyReports } from "../../Redux/Slice/Day";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DynamicTable from "../../Components/Table/DynamicTable";
import Loader from "../../Components/Loader/Loader";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaTimes } from "react-icons/fa";

/* ================= UTC HELPERS ================= */
const getUTCDate = (dateStr) => new Date(dateStr).getUTCDate();
const getUTCMonth = (dateStr) => new Date(dateStr).getUTCMonth();
const getUTCYear = (dateStr) => new Date(dateStr).getUTCFullYear();

const isSundayUTC = (year, month, day) => {
  const d = new Date(Date.UTC(year, month, day));
  return d.getUTCDay() === 0;
};

export const MonthlyReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { AllDailyReportData } = useSelector((state) => state.Day);

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    dispatch(fetchAllDailyReports());
  }, [dispatch]);

  // Get all months & years available
  const monthsAvailableMap = {};
  AllDailyReportData?.data?.forEach((el) => {
    if (!el?.GeneratedDate) return;
    const y = getUTCYear(el.GeneratedDate);
    const m = getUTCMonth(el.GeneratedDate);
    monthsAvailableMap[`${y}-${m}`] = new Date(Date.UTC(y, m, 1));
  });

  const monthsAvailable = Object?.values(monthsAvailableMap)?.sort(
    (a, b) => b - a,
  );

  /* ================= DEFAULT MONTH ================= */
  useEffect(() => {
    if (!selectedMonth && !selectedYear && monthsAvailable.length > 0) {
      const d = monthsAvailable[0];
      setSelectedMonth(d.getUTCMonth());
      setSelectedYear(d.getUTCFullYear());
    }
  }, [monthsAvailable, selectedMonth, selectedYear]);

  /* ================= LOADER ================= */
  if (!AllDailyReportData?.data) {
    return <Loader />;
  }

  /* ================= MONTH FILTER ================= */
  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split("-").map(Number);
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  /* ================= MONTH-WISE GRID ================= */
  const filteredData = AllDailyReportData?.data?.filter((el) => {
    if (!el.GeneratedDate) return false;
    return (
      getUTCMonth(el.GeneratedDate) === selectedMonth &&
      getUTCYear(el.GeneratedDate) === selectedYear
    );
  });

  const monthYear =
    selectedMonth !== null && selectedYear !== null
      ? new Date(selectedYear, selectedMonth).toLocaleString("en-IN", {
        month: "long",
        year: "numeric",
      })
      : "";

  const daysInMonth =
    selectedMonth !== null && selectedYear !== null
      ? new Date(selectedYear, selectedMonth + 1, 0).getDate()
      : 30;

  // Unique members
  const memberMap = {};
  filteredData?.forEach((el) => {
    if (el?.Memberid?._id) {
      memberMap[el.Memberid._id] = {
        MemberId: el.Memberid._id,
        MemberName: el.Memberid.Name,
      };
    }
  });

  let members = Object?.values(memberMap);
  members = members?.sort((a, b) => a.MemberName?.localeCompare(b.MemberName));

  // Call map
  const callMap = {};
  filteredData.forEach((el) => {
    const memberId = el.Memberid?._id;
    const day = getUTCDate(el.GeneratedDate);
    const callNo = el?.WorkId?.CallNo;
    const workId = el?.WorkId?._id;
    const phys = el.Phys;
    const tec = el.Tec;

    if (!memberId || !day || !callNo || !workId) return;

    if (!callMap[memberId]) callMap[memberId] = {};
    if (!callMap[memberId][day]) callMap[memberId][day] = [];

    if (!callMap[memberId][day].some((c) => c.callNo === callNo)) {
      callMap[memberId][day].push({
        callNo,
        workId,
        generatedDate: el.GeneratedDate,
        phys,
        tec,
      });
    }
  });

  // Columns for month-wise table
  const allMemberColumns = [
    { field: "id", headerName: "No", width: 70 },
    { field: "MemberName", headerName: "Name", sticky: true, flex: 1 },
  ];
  for (let d = 1; d <= daysInMonth; d++) {
    allMemberColumns.push({
      field: `day_${d}`,
      headerName: `${d}`,
      width: 100,
      renderCell: (params) => {
        const dayNumber = Number(params.field.split("_")[1]);
        const calls = params.value || [];

        const sunday = isSundayUTC(selectedYear, selectedMonth, dayNumber);

        if (sunday && calls.length === 0) {
          return (
            <span className="text-red-600 py-1 px-2 border-2 rounded border-red-300 font-semibold text-md">
              Sunday
            </span>
          );
        }

        if (calls.length === 0) return "-";

        return (
          <div className="flex flex-wrap gap-1">
            {calls.map((c) => {
              const isVerified = c.phys || c.tec;
              const bgColor = isVerified
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700";

              return (
                <button
                  key={c.callNo}
                  title={`Phys: ${c.phys ? "Verified" : "Pending"}, Tec: ${c.tec ? "Verified" : "Pending"}`}
                  className={`px-2 py-1 ${bgColor} text-white rounded text-sm transition-colors`}
                  onClick={() =>
                    navigate(`/InhouseRnd/CustomerrequustDetail/${c.workId}`)
                  }
                >
                  {c.callNo}
                </button>
              );
            })}
          </div>
        );
      },
    });
  }

  // Rows for month-wise table
  const allMemberRows = members?.map((member, index) => {
    const row = {
      id: index + 1,
      Mainid: member.MemberId,
      MemberName: member.MemberName,
    };
    for (let d = 1; d <= daysInMonth; d++) {
      row[`day_${d}`] = callMap[member.MemberId]?.[d] || [];
    }
    return row;
  });

  /* ================= JSX ================= */
  return (
    <div>
      {/* Title */}
      <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
        Monthly Report
      </h3>

      {/* Month Filter */}
      <div className="mt-5 bg-white shadow-md rounded-xl p-4">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <label className="font-semibold block mb-2">Select Month</label>
            <Autocomplete
              options={monthsAvailable
                .sort((a, b) => b - a)
                .map((date) => ({
                  year: date.getFullYear(),
                  month: date.getMonth(),
                  label: date.toLocaleString("en-IN", {
                    month: "long",
                    year: "numeric",
                  }),
                  value: `${date.getFullYear()}-${date.getMonth()}`,
                }))}
              value={
                selectedMonth !== null && selectedYear !== null
                  ? {
                    year: selectedYear,
                    month: selectedMonth,
                    label: new Date(
                      selectedYear,
                      selectedMonth,
                    ).toLocaleString("en-IN", {
                      month: "long",
                      year: "numeric",
                    }),
                    value: `${selectedYear}-${selectedMonth}`,
                  }
                  : null
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  handleMonthChange({ target: { value: newValue.value } });
                }
              }}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.month === value.month && option.year === value.year
              }
              popupIcon={<FaChevronDown className="text-gray-600 text-sm" />}
              clearIcon={<FaTimes className="text-gray-600 text-xs" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Month"
                  size="small"
                  className="bg-white rounded-md custom-input"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Month-Wise Table */}
      <h3 className="text-lg font-bold my-4">{monthYear}</h3>
      <div className="rounded-lg overflow-x-auto mt-5">
        <DynamicTable
          columns={allMemberColumns}
          rows={allMemberRows}
          searchable={true}
          height={600}
        />
      </div>
    </div>
  );
};

export default MonthlyReport;